import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Newspaper, Eye, Trash2, ShieldOff, RotateCcw, PencilLine,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Pagination } from "@/components/common/Pagination";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { DetailDialog, DetailRow } from "@/components/ui/detail-dialog";
import { DialogShell, Field } from "@/components/common/FormShell";
import { UserName } from "@/components/common/EntityName";
import { postsApi } from "@/lib/api";
import type { Post, PostStatusFilter } from "@/lib/api/types";
import { fmtRelativeTime } from "@/lib/utils";
import { usePagedQuery, PAGE_SIZE } from "@/lib/usePagedQuery";

const FILTERS: { key: PostStatusFilter; label: string }[] = [
  { key: "ALL", label: "All" },
  { key: "LIVE", label: "Live" },
  { key: "DRAFT", label: "Drafts" },
  { key: "TAKEN_DOWN", label: "Taken down" },
];

function StatusBadge({ p }: { p: Post }) {
  if (p.takenDown) return <Badge variant="destructive">Taken down</Badge>;
  if (p.isDraft) return <Badge variant="muted">Draft</Badge>;
  return <Badge variant="success">Live</Badge>;
}

export default function PostsList() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<PostStatusFilter>("ALL");
  const [detail, setDetail] = useState<Post | null>(null);
  const [editing, setEditing] = useState<Post | null>(null);
  const [confirm, setConfirm] = useState<{ kind: "takedown" | "restore" | "delete"; post: Post } | null>(null);

  const {
    page, setPage, items, totalItems, totalPages, isFetching,
  } = usePagedQuery<Post, { status: PostStatusFilter }>({
    key: ["posts", filter],
    fetcher: postsApi.adminList,
    extra: { status: filter },
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["posts"] });
    setConfirm(null);
    setDetail(null);
  };
  const takedown = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => postsApi.takedown(id, reason),
    onSuccess: refresh,
  });
  const restore = useMutation({ mutationFn: (id: string) => postsApi.restore(id), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: string) => postsApi.remove(id), onSuccess: refresh });

  return (
    <>
      <PageHeader
        title="Posts"
        subtitle={`${totalItems} posts. Edit, take down (hidden from the feed with a reason), restore, or delete permanently.`}
      />

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap gap-1.5 border-b border-border p-3">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => { setFilter(f.key); setPage(0); }}
                className={`cursor-pointer rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
                  filter === f.key
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {items.length === 0 ? (
            <EmptyState
              icon={Newspaper}
              title={isFetching ? "Loading…" : "No posts here"}
              description="Nothing matches this filter."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Post</TableHead>
                  <TableHead>Author</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Engagement</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer" onClick={() => setDetail(p)}>
                    <TableCell>
                      <p className="max-w-56 truncate text-sm font-semibold">{p.title}</p>
                      {p.takenDown && p.takedownReason && (
                        <p className="max-w-56 truncate text-xs text-destructive">{p.takedownReason}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-sm"><UserName id={p.authorId} /></TableCell>
                    <TableCell className="text-sm">{p.category ?? "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {p.likesCount ?? 0} likes · {p.commentsCount ?? 0} comments
                    </TableCell>
                    <TableCell><StatusBadge p={p} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtRelativeTime(p.createdAt)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => setEditing(p)}>
                          <PencilLine className="size-3.5" />
                        </Button>
                        {p.takenDown ? (
                          <Button size="sm" variant="outline" onClick={() => setConfirm({ kind: "restore", post: p })}>
                            <RotateCcw className="size-3.5" /> Restore
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="border-warning/50 text-warning" onClick={() => setConfirm({ kind: "takedown", post: p })}>
                            <ShieldOff className="size-3.5" /> Take down
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="border-destructive/40 text-destructive" onClick={() => setConfirm({ kind: "delete", post: p })}>
                          <Trash2 className="size-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDetail(p)}>
                          <Eye className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Pagination page={page} size={PAGE_SIZE} totalItems={totalItems} totalPages={totalPages} onChange={setPage} />
        </CardContent>
      </Card>

      {/* Read-only detail */}
      <DetailDialog
        open={!!detail}
        onClose={() => setDetail(null)}
        icon={Newspaper}
        title={detail?.title ?? ""}
        subtitle={detail ? `${detail.category ?? "General"} · ${fmtRelativeTime(detail.createdAt)}` : undefined}
      >
        {detail && (
          <>
            <div className="mb-2 flex justify-center"><StatusBadge p={detail} /></div>
            <p className="mb-3 max-h-40 overflow-y-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/30 p-3 text-sm">
              {detail.content ?? "—"}
            </p>
            <DetailRow label="Author" value={<UserName id={detail.authorId} />} />
            <DetailRow label="Tags" value={detail.tags?.length ? detail.tags.join(", ") : "—"} />
            <DetailRow label="Likes" value={String(detail.likesCount ?? 0)} />
            <DetailRow label="Comments" value={String(detail.commentsCount ?? 0)} />
            <DetailRow label="Views" value={String(detail.viewsCount ?? 0)} />
            <DetailRow label="Premium" value={detail.isPremium ? "Yes" : "No"} />
            {detail.takenDown && <DetailRow label="Takedown reason" value={detail.takedownReason ?? "—"} />}
          </>
        )}
      </DetailDialog>

      <EditPostDialog post={editing} onOpenChange={(v) => !v && setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />

      <ConfirmDialog
        open={confirm?.kind === "takedown"}
        onOpenChange={(v) => !v && setConfirm(null)}
        onConfirm={(reason) => takedown.mutate({ id: confirm!.post.id, reason })}
        icon={ShieldOff}
        tone="destructive"
        title="Take this post down?"
        description="It disappears from the public feed immediately. You can restore it later — the reason is stored."
        confirmLabel="Take down"
        loading={takedown.isPending}
        withReason
        reasonRequired
        reasonLabel="Reason"
        reasonPlaceholder="e.g. Medical misinformation, spam, inappropriate content…"
      />
      <ConfirmDialog
        open={confirm?.kind === "restore"}
        onOpenChange={(v) => !v && setConfirm(null)}
        onConfirm={() => restore.mutate(confirm!.post.id)}
        icon={RotateCcw}
        tone="success"
        title="Restore this post?"
        description="It returns to the public feed immediately."
        confirmLabel="Restore"
        loading={restore.isPending}
      />
      <ConfirmDialog
        open={confirm?.kind === "delete"}
        onOpenChange={(v) => !v && setConfirm(null)}
        onConfirm={() => remove.mutate(confirm!.post.id)}
        icon={Trash2}
        tone="destructive"
        title="Delete permanently?"
        description="This cannot be undone — prefer Take down unless the content must be erased."
        confirmLabel="Delete forever"
        loading={remove.isPending}
      />
    </>
  );
}

function EditPostDialog({
  post, onOpenChange, onSaved,
}: {
  post: Post | null;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("");
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    if (post) {
      setTitle(post.title);
      setContent(post.content ?? "");
      setCategory(post.category ?? "");
      setIsPremium(!!post.isPremium);
    }
  }, [post]);

  const save = useMutation({
    mutationFn: () =>
      postsApi.update(post!.id, {
        title: title.trim(),
        content: content.trim(),
        category: category.trim() || undefined,
        isPremium,
      }),
    onSuccess: onSaved,
  });

  return (
    <DialogShell
      open={!!post}
      onOpenChange={onOpenChange}
      icon={<PencilLine className="size-7" />}
      title="Edit post"
      description="Changes go live immediately."
      wide
    >
      <div className="mt-5 space-y-3 text-left">
        <Field label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Content">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={6}
            className="w-full rounded-lg border border-border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-primary/40"
          />
        </Field>
        <Field label="Category">
          <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Nutrition" />
        </Field>
        <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
          <input type="checkbox" checked={isPremium} onChange={(e) => setIsPremium(e.target.checked)} className="size-4 accent-[var(--color-primary)]" />
          Premium (subscribers only)
        </label>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="flex-1" disabled={!title.trim() || !content.trim() || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </DialogShell>
  );
}
